import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Loader } from 'components/Loader/Loader'
import { StreamPlayer } from 'components/Player/Player'
import TipModal from 'components/UI/Modal/Tip/TipModal'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { useModalContext } from 'context/modalContext'
import {
  type IssueTokenResponse,
  leaveStreamViewer
} from 'services/endpoints/streaming'
import { useIvsChatStore } from 'store/useIvsChatStore'
import { useViewerStore } from 'store/viewer'
import { type IProfile } from 'types/interfaces/IProfile'

import LiveChat from '../LiveChat/LiveChat'
import EndModalViewer from '../LiveModal/EndModal/EndModalViewer'
import LeaveStream from '../LiveModal/EndModal/LeaveStream'
import { Header } from '../LiveStreamCreator/components/Header/Header'
import PasscodeGate from '../PasscodeGate'

type ViewerCreator = Partial<IProfile> & {
  id?: string
  username?: string
  avatar?: string | { url?: string | null } | null
  active_stream?: boolean
  stream_url?: string | null
}

interface IProps {
  username: string
  creator?: ViewerCreator
}

const MAX_PLAYBACK_RETRIES = 4
const PLAYBACK_RETRY_DELAY_MS = 1500

const ViewerChatLayer = memo(
  ({
    hasEnded,
    creatorId,
    streamId,
  }: {
    hasEnded: boolean
    creatorId?: string | string | null
    streamId?: string | null
  }) => {
    const commentsShown = useViewerStore(s => s.commentsShown)

    return (
      <LiveChat
        visible={!!commentsShown && !hasEnded}
        streamOwnerId={creatorId}
        streamId={streamId}
      />
    )
  }
)

ViewerChatLayer.displayName = 'ViewerChatLayer'

export const LiveStreamViewer = ({ username, creator }: IProps) => {
  const { t } = useTranslation()
  const toggleComments = useViewerStore(s => s.toggleComments)
  const streamId = useViewerStore(s => s.streamId)
  const currentStreamGoal = useViewerStore(s => s.currentStreamGoal)
  const setStreamId = useViewerStore(s => s.setStreamId)
  const clearStreamId = useViewerStore(s => s.clearStreamId)
  const setShowTips = useViewerStore(s => s.setShowTips)

  const { setHideFooter } = useHeaderLayout()
  const modalData = useModalContext()
  const navigate = useNavigate()

  const [tokenUrl, setTokenUrl] = useState<string>('')
  const [hasStreamAccess, setHasStreamAccess] = useState(false)
  const [endModalOpen, setEndModalOpen] = useState(false)
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false)
  const [hasEnded, setHasEnded] = useState(false)
  const [playerInstanceKey, setPlayerInstanceKey] = useState(0)
  const [playbackError, setPlaybackError] = useState<string | null>(null)
  const [isRetryingPlayback, setIsRetryingPlayback] = useState(false)

  const manualLeaveRef = useRef(false)
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef<number | null>(null)
  const leaveSentRef = useRef(false)

  const liveMessages = useIvsChatStore(
    s => s.messagesByStream[String(streamId ?? '')] ?? []
  )

  const realMessagesCount = liveMessages.filter(message => {
    const isTip = (message as any)?.isTip === true
    const isJoin = (message as any)?.isJoin === true
    return !isTip && !isJoin
  }).length

  useEffect(() => {
    setHideFooter(true)
    return () => setHideFooter(false)
  }, [setHideFooter])

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    return () => {
      clearStreamId()
    }
  }, [clearStreamId])

  const avatarUrl = useMemo(() => {
    if (typeof creator?.avatar === 'string') {
      return creator.avatar
    }

    return creator?.avatar?.url ?? ''
  }, [creator])

  const creatorId = useMemo(() => {
    return creator?.id ?? ''
  }, [creator])
  
  const resetPlaybackRetryState = useCallback(() => {
    retryCountRef.current = 0
    setPlaybackError(null)
    setIsRetryingPlayback(false)

    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }, [])

  const leaveStreamOnce = useCallback(async () => {
    const currentStreamId = useViewerStore.getState().streamId

    if (!currentStreamId || leaveSentRef.current) return

    leaveSentRef.current = true

    try {
      await leaveStreamViewer({ stream_id: currentStreamId })
      console.log('[LiveStreamViewer] leaveStreamViewer success', {
        streamId: currentStreamId
      })
    } catch (err) {
      console.error('[LiveStreamViewer] leaveStreamViewer failed', err)
    }
  }, [])

const leaveConfirmOpenRef = useRef(false)
const hasEndedRef = useRef(false)

useEffect(() => {
  leaveConfirmOpenRef.current = leaveConfirmOpen
}, [leaveConfirmOpen])

useEffect(() => {
  hasEndedRef.current = hasEnded
}, [hasEnded])

const handleAutoEnd = useCallback(async () => {
  if (manualLeaveRef.current || leaveConfirmOpenRef.current) {
    return
  }

  await leaveStreamOnce()
  clearStreamId()

  resetPlaybackRetryState()
  setTokenUrl('')
  setHasEnded(true)
  setEndModalOpen(true)
  setLeaveConfirmOpen(false)
}, [leaveStreamOnce, clearStreamId, resetPlaybackRetryState])

  const handleRequestClose = useCallback(() => {
    manualLeaveRef.current = true
    setEndModalOpen(false)
    setLeaveConfirmOpen(true)
  }, [])

  const handleCloseLeaveModal = useCallback(() => {
    manualLeaveRef.current = false
    setLeaveConfirmOpen(false)
  }, [])

  const schedulePlaybackRetry = useCallback(() => {
    if (!tokenUrl) return

    setIsRetryingPlayback(true)
    setPlaybackError(null)

    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current)
    }

    retryTimerRef.current = window.setTimeout(() => {
      setPlayerInstanceKey(prev => prev + 1)
      setIsRetryingPlayback(false)
      retryTimerRef.current = null
    }, PLAYBACK_RETRY_DELAY_MS)
  }, [tokenUrl])

  const handlePlaybackError = useCallback(
    (err?: unknown) => {
      console.error('[LiveStreamViewer] playback fatal error', err)

      if (
        manualLeaveRef.current ||
        leaveConfirmOpenRef.current ||
        hasEndedRef.current
      ) {
        return
      }

      if (retryCountRef.current < MAX_PLAYBACK_RETRIES) {
        retryCountRef.current += 1
        schedulePlaybackRetry()
        return
      }

      setIsRetryingPlayback(false)
      setPlaybackError('Unable to load the live stream right now.')
    },
    [schedulePlaybackRetry]
  )

  const handleRetryNow = useCallback(() => {
    if (!tokenUrl) return
    resetPlaybackRetryState()
    setPlayerInstanceKey(prev => prev + 1)
  }, [resetPlaybackRetryState, tokenUrl])

  const addTip = useCallback(() => {
    modalData.addModal(
      t('tipAmount'),
      <TipModal
        tipType='stream'
        modelData={{
          modelId: creatorId || '',
          avatarSrc: avatarUrl || '',
          stream_id: streamId ?? undefined,
          goal_id: currentStreamGoal?.goal_id ?? undefined,
        }}
      />
    )
  }, [modalData, t, creatorId, avatarUrl, streamId, currentStreamGoal?.goal_id])

  // optional:
  // if you trust Watch's active_stream enough, use it once on mount
  useEffect(() => {
    if (creator?.active_stream === false && !hasEnded && !leaveConfirmOpen) {
      void handleAutoEnd()
    }
  }, [creator?.active_stream, hasEnded, leaveConfirmOpen, handleAutoEnd])

  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentStreamId = useViewerStore.getState().streamId

      if (!currentStreamId || leaveSentRef.current) return

      leaveSentRef.current = true

      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify({ stream_id: currentStreamId })], {
          type: 'application/json'
        })
        navigator.sendBeacon('/api/user/stream/leave', blob)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  const playerNode = useMemo(() => {
    if (hasEnded) return null

    if (!tokenUrl) {
      return <Loader />
    }

    return (
      <>
        <StreamPlayer
          key={`${playerInstanceKey}-${tokenUrl}`}
          source={tokenUrl}
          onEnded={handleAutoEnd}
          onFatalError={handlePlaybackError}
        />

        {(isRetryingPlayback || playbackError) && (
          <PlaybackOverlay>
            {isRetryingPlayback ? (
              <OverlayCard>
                <OverlayText>Trying to reconnect to the live stream…</OverlayText>
              </OverlayCard>
            ) : (
              <OverlayCard>
                <OverlayText>{playbackError}</OverlayText>
                <RetryButton type='button' onClick={handleRetryNow}>
                  Retry
                </RetryButton>
              </OverlayCard>
            )}
          </PlaybackOverlay>
        )}
      </>
    )
  }, [
    hasEnded,
    tokenUrl,
    playerInstanceKey,
    handleAutoEnd,
    handlePlaybackError,
    isRetryingPlayback,
    playbackError,
    handleRetryNow
  ])

  return (
    <Container>
      <Header
        role='viewer'
        creatorUsername={creator?.username ?? username}
        avatarUrl={avatarUrl}
        commentCount={realMessagesCount}
        onToggleComments={toggleComments}
        onOpenTipModal={addTip}
        onRequestClose={handleRequestClose}
      />

      <PlayerContainer>{playerNode}</PlayerContainer>

      {!hasStreamAccess && !hasEnded && (
        <PasscodeGate
          username={username}
onAuthorized={(resp: IssueTokenResponse) => {
  if (hasEnded) return

  resetPlaybackRetryState()
  leaveSentRef.current = false

  setHasStreamAccess(true)

  if (resp?.stream_id) {
    setStreamId(resp.stream_id)
  }

  if (typeof resp?.show_tips === 'boolean') {
    setShowTips(resp.show_tips)
  }

  if (resp.playback_url) {
    setTokenUrl(resp.playback_url)
    setPlayerInstanceKey(prev => prev + 1)
  } else {
    void handleAutoEnd()
  }

  if (resp?.chat?.token) {
    window.__IVS_CHAT__ = {
      region: resp.chat.region ?? 'us-west-2',
      tokenProvider: async () => ({
        token: resp.chat.token,
        sessionExpirationTime: new Date(resp.chat.sessionExpirationTime),
        tokenExpirationTime: new Date(resp.chat.tokenExpirationTime),
      }),
      roomArn: resp.chat.roomArn,
    }
  }
}}
        />
      )}

      <ViewerChatLayer
        hasEnded={hasEnded}
        creatorId={creatorId}
        streamId={streamId}
      />

      {endModalOpen && !leaveConfirmOpen && (
        <EndModalViewer
          onClose={() => setEndModalOpen(false)}
          onConfirm={() => {
            clearStreamId()

            const portal = document.getElementById('portal')
            if (portal && portal.classList.contains('portal--open')) {
              portal.classList.remove('portal--open')
            }

            navigate(-1)
          }}
        />
      )}

      {leaveConfirmOpen && (
        <LeaveStream
          onClose={handleCloseLeaveModal}
          onConfirm={async () => {
            await leaveStreamOnce()
            clearStreamId()

            const portal = document.getElementById('portal')
            if (portal && portal.classList.contains('portal--open')) {
              portal.classList.remove('portal--open')
            }

            navigate(-1)
          }}
        />
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: fixed;
  inset: 0;
  background-color: black;
`

const PlayerContainer = styled.div`
  position: relative;
  height: 100vh;
  max-height: 100vh;
  width: 100vw;
  max-width: 100vw;
`

const PlaybackOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

const OverlayCard = styled.div`
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  padding: 16px 18px;
  border-radius: 12px;
  min-width: 240px;
  text-align: center;
`

const OverlayText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
`

const RetryButton = styled.button`
  margin-top: 12px;
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  background: #2e97fe;
  color: #fff;
  font-weight: 600;
`